import React from 'react';
import { connect } from 'react-redux';
import CopyToClipboard from 'react-copy-to-clipboard';
import styled from 'styled-components';

import { ContentSet, HelperBlock } from 'components/Shared/Scaffolding';
import Panel, { PanelTitle } from 'components/Shared/Panel';
import { TextButton } from 'components/Shared/Buttons';
import notify from 'utils/notify';
import { SCAFFOLDING, SECTIONS } from 'theme';
import { currentMapIdState } from 'modules/map';
import { useRecoilState } from 'recoil';
import { getAbsoluteUrl } from 'utils/urls';
import { HtmlHighlight } from 'components/Export/HtmlHighlight';

const CodeBlock = styled.div`
    margin-left: -${SCAFFOLDING.GUTTER};
    margin-right: -${SCAFFOLDING.GUTTER};
    padding: ${SCAFFOLDING.GUTTER_FACTOR(0.5)} ${SCAFFOLDING.GUTTER};
    background-color: ${SECTIONS.SUB.BG};

    pre {
        padding: 0;
    }
`;

const StyledTextButton = styled(TextButton)`
    width: 100%;
    margin-top: ${SCAFFOLDING.GUTTER_FACTOR(0.5)};
    margin-bottom: ${SCAFFOLDING.GUTTER_FACTOR(0.5)};
    background-color: transparent;
    font-size: 0.85em;

    &:hover,
    &:focus {
        background-color: transparent;
    }
`;

const StyledHelperBlock = styled(HelperBlock)`
    margin-left: -${SCAFFOLDING.GUTTER};
    margin-right: -${SCAFFOLDING.GUTTER};
`;

const ExportPanel = ({ width, height, open, onClose, onCopy }) => {
    const [mapId] = useRecoilState(currentMapIdState);
    const embedUrl = getAbsoluteUrl('/embed/' + mapId);
    const previewUrl = getAbsoluteUrl('/build-a-map/preview/' + mapId);
    const iframeText = `<iframe src="${embedUrl}" width="${width}" height="${height}" style="border:none;"></iframe>`;
    return (
        <Panel open={open} onClose={onClose}>
            <ContentSet spacing={SCAFFOLDING.GUTTER} padding={SCAFFOLDING.GUTTER}>
                <PanelTitle>View Code</PanelTitle>
                <StyledHelperBlock highlight="info">
                    <strong>Make sure to save all of your changes before proceeding.</strong>
                </StyledHelperBlock>
                <div>
                    <p>
                        You can embed your map by copying the HTML below into your site.{' '}
                        <a href={previewUrl} title="Embedded map preview" target="_blank" rel="noopener noreferrer">
                            Click here
                        </a>{' '}
                        to view a preview of your map embedded in a page. Make sure to save all changes to ensure the
                        preview is up to date.
                    </p>
                    <p>
                        The actual size of your map may vary depending on the HTML and CSS of your site. Make sure your
                        site can handle the sizing of the map appropriately. If any changes are made to the size of the
                        map, the embedded code on your site will need to be updated.
                    </p>
                </div>
                <CodeBlock>
                    <div>HTML:</div>
                    <CopyToClipboard text={iframeText} onCopy={onCopy}>
                        <StyledTextButton>Copy Code To Clipboard</StyledTextButton>
                    </CopyToClipboard>
                    <HtmlHighlight content={iframeText} />
                </CodeBlock>
            </ContentSet>
        </Panel>
    );
};

const mapStateToProps = (state) => {
    const size = state.map.size;
    const di = (name) => `${size[name]}${size[`${name}Unit`]}`;
    return {
        width: di('width'),
        height: di('height'),
    };
};

const mapDispatchToProps = () => ({
    onCopy: (text, result) => {
        if (result) {
            notify.success('Copied to clipboard!');
        }
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(ExportPanel);
